<template>
  <div class="row mt-4" v-if="pedidos.length || loading">
    <div class="col-12">
      <div class="card ficha-listado">
        <div class="px-4 pb-4 card-body">

          <!-- Cabecera -->
          <div class="d-flex justify-content-between align-items-center mb-3">
            <div>
              <h6 class="mb-0 panel-section-title">Pedidos del período</h6>
              <p class="text-xs opacity-7 mb-0">
                {{ pedidos.length >= 500 ? 'Mostrando los últimos 500 registros' : `${pedidos.length} pedidos` }}
              </p>
            </div>
            <button class="btn-descargar" :disabled="descargando" @click="descargar">
              <span v-if="descargando" class="spinner-border spinner-border-sm me-1"></span>
              <i v-else class="fas fa-download me-1"></i>
              {{ descargando ? 'Generando…' : 'Descargar Excel' }}
            </button>
          </div>

          <!-- Buscador + leyenda de estados -->
          <div class="toolbar-pedidos mb-3">
            <input
              v-model="filtroTexto"
              type="text"
              class="input-buscar"
              placeholder="Buscar por referencia, email, Telegram, producto…"
            />
            <div class="leyenda-estados">
              <button class="ley-item" :class="{ 'ley-activo': filtroEstado === 'C' }" @click="filtrarEstado('C')">
                <span class="badge-estado badge-confirmado">Confirmado</span>
              </button>
              <button class="ley-item" :class="{ 'ley-activo': filtroEstado === 'P' }" @click="filtrarEstado('P')">
                <span class="badge-estado badge-pendiente">Pendiente</span>
              </button>
              <button class="ley-item" :class="{ 'ley-activo': filtroEstado === 'dev' }" @click="filtrarEstado('dev')">
                <span class="badge-estado badge-devuelto">Devuelto</span>
              </button>
              <button class="ley-item" :class="{ 'ley-activo': filtroEstado === 'F' }" @click="filtrarEstado('F')">
                <span class="badge-estado badge-fallido">Fallido</span>
              </button>
              <button class="ley-item" :class="{ 'ley-activo': filtroEstado === '' }" @click="filtrarEstado('')">
                <span class="badge-estado badge-todos">Todos</span>
              </button>
            </div>
          </div>

          <!-- Tabla -->
          <div class="tabla-wrap">
            <table class="tabla-pedidos">
              <thead>
                <tr>
                  <th class="sin-ord"></th>
                  <th @click="ordenar('fecha')" class="th-ord">Fecha <span class="ord-icon">{{ iconOrden('fecha') }}</span></th>
                  <th>Referencia</th>
                  <th @click="ordenar('producto')" class="th-ord">Producto <span class="ord-icon">{{ iconOrden('producto') }}</span></th>
                  <th @click="ordenar('precio')" class="th-ord">Precio <span class="ord-icon">{{ iconOrden('precio') }}</span></th>
                  <th class="sin-ord">Últ. cobro</th>
                  <th @click="ordenar('estado')" class="th-ord">Pedido inicial <span class="ord-icon">{{ iconOrden('estado') }}</span></th>
                  <th class="sin-ord">Estado suscripción</th>
                  <th @click="ordenar('cliente_email')" class="th-ord">Email <span class="ord-icon">{{ iconOrden('cliente_email') }}</span></th>
                  <th @click="ordenar('pais_compra')" class="th-ord">País <span class="ord-icon">{{ iconOrden('pais_compra') }}</span></th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="p in pedidosPaginados" :key="p.id">
                  <td class="td-ver">
                    <button class="btn-ver" @click="abrirDetalle(p)" title="Ver detalle">
                      <i class="fas fa-eye"></i>
                    </button>
                  </td>
                  <td class="td-fecha">{{ fmtFecha(p.fecha) }}</td>
                  <td class="td-ref">{{ p.referencia }}</td>
                  <td class="td-prod">
                    <span v-if="p.es_suscripcion" class="badge-tipo suscripcion" title="Suscripción recurrente">🔄</span>
                    <span v-else class="badge-tipo unico" title="Pago único">⚡</span>
                    {{ p.producto }}
                  </td>
                  <td class="td-precio">{{ fmtMoney(p.precio_lista ?? p.precio, p.moneda) }}</td>
                  <td class="td-pagado">
                    <div class="pagado-wrap">
                      <span v-if="p.es_trial" class="badge-trial" title="Trial activo — aún no cobrado">Trial</span>
                      <template v-else-if="p.estado === 'C'">
                        <span class="precio-pagado">{{ fmtMoney(p.precio_pagado, p.moneda) }}</span>
                        <small v-if="p.motivo_descuento === 'cupon'" class="motivo-tag">Cupón</small>
                      </template>
                      <template v-else>
                        <span class="precio-pagado sin-cobro">—</span>
                      </template>
                    </div>
                  </td>
                  <td>
                    <span class="badge-estado" :class="badgeEstado(p)">
                      {{ labelEstado(p) }}
                    </span>
                  </td>
                  <td class="td-susc">
                    <div v-if="p.es_suscripcion && p.susc_estado" class="td-susc-cel">
                      <span :class="['badge-susc', 'badge-susc-' + p.susc_estado]">{{ p.susc_estado }}</span>
                      <span v-if="p.susc_cancel_prog" class="td-susc-cancel-prog">En proceso de cancelación</span>
                    </div>
                    <span v-else class="td-susc-na">—</span>
                  </td>
                  <td class="td-email">{{ p.cliente_email || '—' }}</td>
                  <td class="td-pais">
                    <span v-if="p.pais_compra" :class="'fi fi-' + p.pais_compra.toLowerCase() + ' fis flag-icon'"></span>
                    {{ p.pais_compra || '—' }}
                  </td>
                </tr>
                <tr v-if="pedidosFiltrados.length === 0">
                  <td colspan="10" class="td-empty">Sin resultados</td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Paginación -->
          <div v-if="totalPaginas > 1" class="tp-paginacion">
            <button class="btn-pag" :disabled="pagina === 1" @click="pagina--">‹</button>
            <span class="pag-info">{{ pagina }} / {{ totalPaginas }}</span>
            <button class="btn-pag" :disabled="pagina === totalPaginas" @click="pagina++">›</button>
          </div>

        </div>
      </div>
    </div>
  </div>

  <!-- Modal detalle pedido -->
  <div v-if="modalDetalle" class="q-modal-overlay" @click.self="cerrarDetalle">
    <div class="q-modal">
      <div class="q-modal-head">
        <div>
          <h6 class="mb-0 panel-section-title">Detalle del pedido</h6>
          <p class="text-sm opacity-7 mb-0">{{ modalPedido?.referencia || '—' }}</p>
        </div>
        <button class="q-modal-close" @click="cerrarDetalle">✕</button>
      </div>

      <div class="q-modal-body">
        <!-- Info general -->
        <div class="det-grid">
          <div class="det-field">
            <div class="det-label">Producto</div>
            <div class="det-value">{{ modalPedido?.producto || '—' }}</div>
          </div>
          <div class="det-field">
            <div class="det-label">Fecha</div>
            <div class="det-value">{{ fmtFecha(modalPedido?.fecha) }}</div>
          </div>
          <div class="det-field">
            <div class="det-label">Precio</div>
            <div class="det-value det-precio">{{ fmtMoney(modalPedido?.precio_lista ?? modalPedido?.precio, modalPedido?.moneda) }}</div>
          </div>
          <div class="det-field">
            <div class="det-label">Último cobro</div>
            <div class="det-value det-precio">
              <template v-if="modalPedido?.es_trial">
                <span class="badge-trial">Trial — sin cobro</span>
              </template>
              <template v-else-if="modalPedido?.estado === 'C'">
                {{ fmtMoney(modalPedido?.precio_pagado, modalPedido?.moneda) }}
                <small v-if="modalPedido?.motivo_descuento === 'cupon'" class="motivo-tag ms-1">Cupón</small>
                <small v-if="modalPedido?.ultimo_cobro_fecha" class="det-cobro-fecha">{{ fmtFecha(modalPedido.ultimo_cobro_fecha) }}</small>
              </template>
              <template v-else>—</template>
            </div>
          </div>
          <div class="det-field">
            <div class="det-label">Pedido inicial</div>
            <div class="det-value">
              <span class="badge-estado" :class="badgeEstado(modalPedido)">{{ labelEstado(modalPedido) }}</span>
            </div>
          </div>
          <div class="det-field">
            <div class="det-label">Email</div>
            <div class="det-value">{{ modalPedido?.cliente_email || '—' }}</div>
          </div>
          <div class="det-field">
            <div class="det-label">Telegram ID</div>
            <div class="det-value det-mono">{{ modalPedido?.telegram_id || '—' }}</div>
          </div>
          <div class="det-field" v-if="modalPedido?.telegram_nombre || modalPedido?.telegram_username">
            <div class="det-label">Nick Telegram</div>
            <div class="det-value">
              <span v-if="modalPedido.telegram_username" class="det-mono">@{{ modalPedido.telegram_username }}</span>
              <span v-else>{{ modalPedido.telegram_nombre }}</span>
              <span v-if="modalPedido.telegram_username && modalPedido.telegram_nombre" class="det-tg-nombre"> · {{ modalPedido.telegram_nombre }}</span>
            </div>
          </div>
          <div class="det-field">
            <div class="det-label">País</div>
            <div class="det-value">
              <span v-if="modalPedido?.pais_compra">
                <span :class="'fi fi-' + modalPedido.pais_compra.toLowerCase() + ' fis flag-icon me-1'"></span>
                {{ modalPedido.pais_compra }}
              </span>
              <span v-else>—</span>
            </div>
          </div>
          <div class="det-field" v-if="modalPedido?.stripe_subscription_id">
            <div class="det-label">Suscripción Stripe</div>
            <div class="det-value det-mono det-small">{{ modalPedido.stripe_subscription_id }}</div>
          </div>
          <div class="det-field" v-if="modalPedido?.proximo_pago">
            <div class="det-label">Próximo pago</div>
            <div class="det-value det-proximo-pago">{{ fmtFechaSolo(modalPedido.proximo_pago) }}</div>
          </div>
        </div>

        <!-- Motivo del fallo (solo pedidos Fallidos) -->
        <div v-if="modalPedido?.estado === 'F'" class="det-section mt-4">
          <div class="det-section-title">Motivo del fallo</div>
          <div v-if="stripeErrorLoading" class="stripe-loading">
            <span class="spinner-border spinner-border-sm me-2"></span>Consultando Stripe…
          </div>
          <div v-else-if="stripeErrorMsg" class="stripe-error-box">
            {{ stripeErrorMsg }}
          </div>
          <div v-else class="det-empty">No se pudo obtener el motivo del fallo.</div>
        </div>

        <!-- Enlace de invitación -->
        <div class="det-section mt-4">
          <div class="det-section-title">Enlace de invitación al canal</div>

          <!-- Sin enlace -->
          <div v-if="!modalPedido?.invite_link && !enlaceAnterior" class="det-empty">
            Este pedido no tiene enlace de invitación generado.
          </div>

          <!-- Estado normal (antes de regenerar) -->
          <div v-else-if="!nuevoEnlace" class="det-invite">
            <div class="det-invite-row">
              <div class="det-label">Enlace actual</div>
              <a :href="modalPedido.invite_link" target="_blank" rel="noopener" class="det-link">
                {{ modalPedido.invite_link }}
              </a>
            </div>

            <div class="det-invite-row mt-2">
              <div class="det-label">Estado del enlace</div>
              <div v-if="modalPedido.invite_used_at || modalPedido.telegram_id" class="det-usado">
                <span class="badge-usado">Usado</span>
                <span class="det-usado-info">
                  <template v-if="modalPedido.invite_used_at">el {{ fmtFecha(modalPedido.invite_used_at) }}&nbsp;</template>
                  <template v-if="modalPedido.telegram_id">
                    por Telegram ID <b class="det-mono">{{ modalPedido.telegram_id }}</b>
                  </template>
                </span>
              </div>
              <div v-else class="det-sin-usar">
                <span class="badge-sinusar">Sin usar</span>
                <span class="det-usado-info">El usuario aún no ha accedido al canal</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Histórico de pagos (inline, solo suscripciones) -->
        <div v-if="modalPedido?.es_suscripcion" class="det-section mt-4">
          <div class="det-section-title">Histórico de pagos</div>
          <div v-if="historialLoading" class="hist-loading">
            <span class="spinner-border spinner-border-sm me-2"></span>Cargando…
          </div>
          <div v-else-if="historialEventos.length === 0" class="det-empty">Sin registros de pagos.</div>
          <div v-else class="hist-timeline">
            <div v-for="(ev, idx) in historialEventos" :key="idx" class="hist-item" :class="'hist-' + ev.tipo">
              <div class="hist-dot"></div>
              <div class="hist-content">
                <div class="hist-label">{{ labelEvento(ev.tipo, idx) }}</div>
                <div class="hist-fecha">{{ fmtFecha(ev.fecha) }}</div>
                <div v-if="ev.importe !== null && ev.importe > 0" class="hist-importe">
                  {{ fmtMoney(ev.importe, ev.moneda) }}
                </div>
                <div v-else-if="ev.es_trial" class="hist-importe hist-importe--trial">Trial gratuito</div>
                <div v-if="ev.tipo === 'pago_fallido' && ev.motivo" class="hist-motivo">{{ ev.motivo }}</div>
              </div>
            </div>
          </div>
        </div>

        <!-- Resultado regeneración -->
        <div v-if="regenMsg" class="det-regen-msg mt-3" :class="regenOk ? 'det-regen-ok' : 'det-regen-err'">
          {{ regenMsg }}
        </div>
        <div v-if="nuevoEnlace" class="det-regen-enlaces mt-3">
          <div class="det-invite-row">
            <div class="det-label">Enlace anterior <span class="badge-revocado">Revocado</span></div>
            <span class="det-link det-link--revocado">{{ enlaceAnterior || '—' }}</span>
          </div>
          <div class="det-invite-row mt-2">
            <div class="det-label">Nuevo enlace <span class="badge-nuevo">Uso único</span></div>
            <a :href="nuevoEnlace" target="_blank" rel="noopener" class="det-link det-link--nuevo">{{ nuevoEnlace }}</a>
          </div>
        </div>
      </div>

      <div class="q-modal-foot">
        <button v-if="modalPedido?.canal_id && modalPedido?.telegram_id" class="btn-regen" :disabled="actualizandoNick" @click="actualizarNick">
          <span v-if="actualizandoNick" class="spinner-border spinner-border-sm me-1"></span>
          <span v-else>📋 Actualizar nick</span>
        </button>
        <button v-if="modalPedido?.invite_link || modalPedido?.canal_id" class="btn-regen" :disabled="regenerando" @click="regenerarEnlace">
          <span v-if="regenerando" class="spinner-border spinner-border-sm me-1"></span>
          <i v-else class="fas fa-rotate me-1"></i>
          {{ regenerando ? 'Regenerando…' : 'Regenerar enlace' }}
        </button>
        <button class="btn-cerrar" @click="cerrarDetalle">Cerrar</button>
      </div>
    </div>
  </div>

</template>

<script>
import axios from "axios";
import showSwal from "/src/mixins/showSwal.js";
import panelesService from "@/services/paneles.service.js";

const POR_PAGINA = 10;

export default {
  name: "PanelTipsterPedidos",

  props: {
    pedidos:   { type: Array,  default: () => [] },
    loading:   { type: Boolean, default: false },
    canAdmin:  { type: Boolean, default: false },
    tipsterId: { type: [Number, String], default: null },
    dateFrom:  { type: String, default: null },
    dateTo:    { type: String, default: null },
  },

  data() {
    return {
      filtroTexto: "",
      filtroEstado: "C",
      ordenCol: "fecha",
      ordenDir: "desc",
      pagina: 1,
      descargando: false,
      // modal detalle
      modalDetalle: false,
      modalPedido: null,
      regenerando: false,
      regenMsg: "",
      regenOk: false,
      nuevoEnlace: "",
      enlaceAnterior: "",
      actualizandoNick: false,
      stripeErrorMsg: null,
      stripeErrorLoading: false,
      historialLoading: false,
      historialEventos: [],
    };
  },

  computed: {
    pedidosFiltrados() {
      const txt = (this.filtroTexto || "").toLowerCase().trim();
      let lista = [...this.pedidos];

      if (txt) {
        lista = lista.filter(p =>
          (p.referencia    || "").toLowerCase().includes(txt) ||
          (p.cliente_email || "").toLowerCase().includes(txt) ||
          (p.telegram_id   || "").toString().includes(txt)    ||
          (p.producto      || "").toLowerCase().includes(txt)
        );
      }

      if (this.filtroEstado === "C")   lista = lista.filter(p => p.estado === "C" && !p.es_devuelto);
      if (this.filtroEstado === "P")   lista = lista.filter(p => p.estado === "P");
      if (this.filtroEstado === "dev") lista = lista.filter(p => !!p.es_devuelto);
      if (this.filtroEstado === "F")   lista = lista.filter(p => p.estado === "F");

      lista.sort((a, b) => {
        let va = a[this.ordenCol] ?? "";
        let vb = b[this.ordenCol] ?? "";
        if (this.ordenCol === "precio") { va = Number(va); vb = Number(vb); }
        if (va < vb) return this.ordenDir === "asc" ? -1 : 1;
        if (va > vb) return this.ordenDir === "asc" ?  1 : -1;
        return 0;
      });

      return lista;
    },

    totalPaginas() {
      return Math.max(1, Math.ceil(this.pedidosFiltrados.length / POR_PAGINA));
    },

    pedidosPaginados() {
      const ini = (this.pagina - 1) * POR_PAGINA;
      return this.pedidosFiltrados.slice(ini, ini + POR_PAGINA);
    },
  },

  watch: {
    filtroTexto()  { this.pagina = 1; },
    filtroEstado() { this.pagina = 1; },
    pedidos()      { this.pagina = 1; },
  },

  methods: {
    ordenar(col) {
      if (this.ordenCol === col) {
        this.ordenDir = this.ordenDir === "asc" ? "desc" : "asc";
      } else {
        this.ordenCol = col;
        this.ordenDir = ["fecha", "precio"].includes(col) ? "desc" : "asc";
      }
      this.pagina = 1;
    },

    iconOrden(col) {
      if (this.ordenCol !== col) return "↕";
      return this.ordenDir === "asc" ? "↑" : "↓";
    },

    fmtFecha(f) {
      if (!f) return "—";
      if (typeof f !== "string") f = String(f);
      const m = f.match(/^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})/);
      if (!m) return f;
      return `${m[3]}/${m[2]}/${m[1]}, ${m[4]}:${m[5]}`;
    },

    fmtFechaSolo(f) {
      if (!f) return "—";
      // Tomar solo la parte de fecha (YYYY-MM-DD) para evitar desfase horario
      const parte = typeof f === "string" ? f.slice(0, 10) : new Date(f).toISOString().slice(0, 10);
      const [y, m, d] = parte.split("-");
      return `${d}/${m}/${y}`;
    },

    fmtEur(n) {
      return Number(n || 0).toLocaleString("es-ES", { style: "currency", currency: "EUR" });
    },
    fmtMoney(n, moneda) {
      const cur = (moneda || 'EUR').toUpperCase();
      return Number(n || 0).toLocaleString(cur === 'USD' ? 'en-US' : 'es-ES', { style: "currency", currency: cur });
    },

    labelEstado(p) {
      if (!p) return "—";
      if (p.es_devuelto) return "Devuelto";
      if (p.es_trial)    return "Trial activo";
      if (p.estado === "C") return "Confirmado";
      if (p.estado === "P") return "Pendiente";
      if (p.estado === "F") return "Fallido";
      return p.estado || "—";
    },

    badgeEstado(p) {
      if (!p) return "badge-neutro";
      if (p.es_devuelto) return "badge-devuelto";
      if (p.es_trial)    return "badge-trial";
      if (p.estado === "C") return "badge-confirmado";
      if (p.estado === "P") return "badge-pendiente";
      if (p.estado === "F") return "badge-fallido";
      return "badge-neutro";
    },

    filtrarEstado(val) {
      this.filtroEstado = val;
      this.pagina = 1;
    },

    abrirDetalle(p) {
      this.modalPedido        = { ...p };
      this.regenMsg           = "";
      this.regenOk            = false;
      this.nuevoEnlace        = "";
      this.enlaceAnterior     = "";
      this.stripeErrorMsg     = null;
      this.stripeErrorLoading = false;
      this.historialEventos   = [];
      this.modalDetalle       = true;
      if (p.estado === 'F') this.fetchStripeError();
      if (p.es_suscripcion)  this.cargarHistorial();
    },

    cerrarDetalle() {
      this.modalDetalle       = false;
      this.modalPedido        = null;
      this.regenMsg           = "";
      this.nuevoEnlace        = "";
      this.enlaceAnterior     = "";
      this.stripeErrorMsg     = null;
    },

    async fetchStripeError() {
      if (!this.tipsterId || !this.modalPedido?.id) return;
      if (!this.modalPedido.paymentId) {
        this.stripeErrorMsg = 'Pago abandonado — el cliente no completó el proceso de pago.';
        return;
      }
      this.stripeErrorLoading = true;
      this.stripeErrorMsg     = null;
      try {
        const token = JSON.parse(localStorage.getItem("userF"));
        const res = await axios.get(
          `${process.env.VUE_APP_API_BASE_URL}/api/v2/tipster/panel/${this.tipsterId}/stripe-error/${this.modalPedido.id}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        this.stripeErrorMsg = res.data?.motivo || res.data?.mensaje || 'Sin información disponible.';
      } catch (e) {
        this.stripeErrorMsg = e?.response?.data?.mensaje || 'Error al consultar Stripe.';
      } finally {
        this.stripeErrorLoading = false;
      }
    },

    async regenerarEnlace() {
      if (!this.modalPedido) return;
      this.regenerando    = true;
      this.regenMsg       = "";
      this.regenOk        = false;
      this.nuevoEnlace    = "";
      this.enlaceAnterior = "";
      try {
        const tipsterId = this.canAdmin && this.tipsterId ? this.tipsterId : null;
        const data = await panelesService.regenerarEnlacePedido(this.modalPedido.id, tipsterId);
        if (data?.res === 'OK') {
          this.regenOk        = true;
          this.regenMsg       = data.mensaje || "Enlace regenerado correctamente";
          this.enlaceAnterior = this.modalPedido.invite_link || "";
          this.nuevoEnlace    = data.invite_link || "";
          // actualizar el pedido en el modal con el nuevo enlace
          this.modalPedido.invite_link    = data.invite_link || null;
          this.modalPedido.invite_used_at = null;
          if (data.kick_ok) this.modalPedido.telegram_id = null;
        } else {
          this.regenOk  = false;
          this.regenMsg = data?.mensaje || "No se pudo regenerar el enlace";
        }
      } catch (e) {
        this.regenOk  = false;
        this.regenMsg = e?.response?.data?.mensaje || "Error al regenerar el enlace";
      } finally {
        this.regenerando = false;
      }
    },

    async actualizarNick() {
      if (!this.modalPedido?.canal_id || !this.tipsterId || this.actualizandoNick) return;
      this.actualizandoNick = true;
      try {
        const token = JSON.parse(localStorage.getItem("userF"));
        const res = await axios.post(
          `${process.env.VUE_APP_API_BASE_URL}/api/v2/tipster/panel/${this.tipsterId}/actualizar-nicks`,
          { canal_id: this.modalPedido.canal_id },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (res.data?.res === 'OK') {
          showSwal.methods.showSwal({ type: "success", message: res.data.mensaje, width: 500 });
        } else {
          showSwal.methods.showSwal({ type: "error", message: res.data?.mensaje || "Error al encolar la actualización.", width: 500 });
        }
      } catch (e) {
        showSwal.methods.showSwal({ type: "error", message: e?.response?.data?.mensaje || "Error al actualizar el nick.", width: 500 });
      } finally {
        this.actualizandoNick = false;
      }
    },

    async cargarHistorial() {
      if (!this.modalPedido?.id) return;
      this.historialLoading = true;
      this.historialEventos = [];
      try {
        const token = JSON.parse(localStorage.getItem("userF"));
        const res = await axios.get(
          `${process.env.VUE_APP_API_BASE_URL}/api/v2/tipster/panel/${this.tipsterId}/pedido/${this.modalPedido.id}/historial`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        this.historialEventos = res.data?.eventos || [];
      } catch (e) {
        this.historialEventos = [];
      } finally {
        this.historialLoading = false;
      }
    },

    labelEvento(tipo, idx) {
      if (tipo === 'pago_ok' || tipo === 'pago_recuperado') {
        const pagosPrevios = this.historialEventos
          .slice(0, idx)
          .filter(e => e.tipo === 'pago_ok' || e.tipo === 'pago_recuperado').length;
        if (tipo === 'pago_recuperado') return 'Pago recuperado';
        return pagosPrevios === 0 ? 'Primer pago' : 'Renovación';
      }
      return { creada: 'Alta', pago_fallido: 'Pago fallido', cancelada: 'Cancelada', vencida: 'Vencida' }[tipo] || tipo;
    },

    async descargar() {
      this.descargando = true;
      try {
        const payload = {};
        if (this.dateFrom) payload.date_from = this.dateFrom;
        if (this.dateTo)   payload.date_to   = this.dateTo;

        let response;
        if (this.canAdmin && this.tipsterId) {
          response = await panelesService.exportarPedidosPanelAdmin(this.tipsterId, payload);
        } else {
          response = await panelesService.exportarPedidosPanel(payload);
        }

        const url  = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href  = url;
        link.setAttribute("download", "pedidos_panel_" + new Date().toISOString().slice(0, 10) + ".xlsx");
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      } catch {
        showSwal.methods.showSwal({ type: "error", message: "No se pudo generar el archivo.", width: 400 });
      } finally {
        this.descargando = false;
      }
    },
  },
};
</script>

<style scoped>
.tabla-wrap {
  overflow-x: auto;
}

.tabla-pedidos {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.82rem;
  min-width: 820px;
}

.tabla-pedidos th {
  background: var(--q-card-alt);
  border-bottom: 1px solid var(--q-border);
  padding: 8px 10px;
  text-align: left;
  font-weight: 700;
  font-size: 0.78rem;
  letter-spacing: 0;
  color: var(--q-muted);
  white-space: nowrap;
}

.th-ord { cursor: pointer; user-select: none; }
.th-ord:hover { color: var(--q-gold); }
.ord-icon { font-size: 0.7rem; opacity: .7; }

.tabla-pedidos td {
  padding: 8px 10px;
  border-bottom: 1px solid var(--q-border);
  color: var(--q-text);
  vertical-align: middle;
}

.tabla-pedidos tbody tr:hover { background: var(--q-hover); }

.td-fecha  { white-space: nowrap; font-size: 0.78rem; color: var(--q-muted); }
.td-ref    { font-family: var(--q-font-mono); font-size: 0.78rem; }
.td-prod   { max-width: 160px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.td-precio { font-weight: 700; color: var(--q-gold); font-family: var(--q-font-mono); white-space: nowrap; }
.td-tg     { font-family: var(--q-font-mono); font-size: 0.78rem; color: var(--q-muted); }
.td-email  { font-size: 0.78rem; max-width: 180px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.td-pais   { font-size: 0.78rem; color: var(--q-muted); white-space: nowrap; }
.td-susc   { white-space: nowrap; }
.td-susc-na { color: var(--q-muted); }
.td-susc-cel { display: flex; flex-direction: column; align-items: flex-start; gap: 3px; }
.td-susc-cancel-prog { font-size: 0.68rem; color: #f87171; }
.td-empty  { text-align: center; padding: 2rem; color: var(--q-muted); }

/* Estado suscripción badges */
.badge-susc {
  display: inline-block; padding: 2px 9px; border-radius: 20px;
  font-size: 0.72rem; font-weight: 700; white-space: nowrap;
}
.badge-susc-activa    { background: rgba(34,197,94,.1);  color: #22c55e; border: 1px solid rgba(34,197,94,.3); }
.badge-susc-trialing  { background: rgba(139,92,246,.1); color: #8b5cf6; border: 1px solid rgba(139,92,246,.3); }
.badge-susc-gracia    { background: rgba(245,158,11,.1); color: #f59e0b; border: 1px solid rgba(245,158,11,.3); }
.badge-susc-vencida   { background: rgba(239,68,68,.1);  color: #ef4444; border: 1px solid rgba(239,68,68,.3); }
.badge-susc-cancelada { background: rgba(107,114,128,.1);color: #6b7280; border: 1px solid rgba(107,114,128,.3); }
.badge-susc-desconocido { background: rgba(107,114,128,.1); color: #6b7280; border: 1px solid rgba(107,114,128,.3); }

/* Flag icon */
.flag-icon { margin-right: 4px; }

/* Badges de estado */
.badge-estado {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 20px;
  font-size: 0.72rem;
  font-weight: 700;
  white-space: nowrap;
}
.badge-confirmado { background: var(--q-green-soft); color: var(--q-green); }
.badge-pendiente  { background: var(--q-amber-soft); color: var(--q-amber); }
.badge-neutro     { background: var(--q-hover);       color: var(--q-muted); }
.badge-devuelto   { background: var(--q-red-soft, rgba(239,68,68,.15)); color: var(--q-red, #ef4444); }
.badge-fallido    { background: rgba(220,60,30,.12); color: #d63b1e; }
.badge-todos      { background: var(--q-hover);       color: var(--q-muted); }

/* Toolbar buscador + leyenda */
.toolbar-pedidos {
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
}

/* Buscador */
.input-buscar {
  flex: 1 1 220px;
  max-width: 380px;
  height: 36px;
  background: var(--q-card-alt);
  border: 1px solid var(--q-border);
  border-radius: 8px;
  padding: 0 12px;
  font-size: 0.85rem;
  color: var(--q-text);
}
.input-buscar:focus {
  outline: none;
  border-color: var(--q-gold);
}

/* Leyenda de estados */
.leyenda-estados {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  flex-shrink: 0;
}
.ley-item {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 0.75rem;
  color: var(--q-muted);
  white-space: nowrap;
  background: transparent;
  border: 1.5px solid var(--q-border);
  border-radius: 20px;
  padding: 3px 10px;
  cursor: pointer;
  transition: border-color .15s, background .15s;

  &:hover { border-color: var(--q-border-md); }

  &.ley-activo {
    border-color: var(--q-border-md);
    background: var(--q-hover);
  }
}
/* Borde activo con el color del badge */
.ley-item.ley-activo:has(.badge-confirmado) { border-color: var(--q-green); }
.ley-item.ley-activo:has(.badge-pendiente)  { border-color: var(--q-amber); }
.ley-item.ley-activo:has(.badge-devuelto)   { border-color: var(--q-red, #ef4444); }
.ley-item.ley-activo:has(.badge-fallido)    { border-color: #d63b1e; }
.ley-item.ley-activo:has(.badge-todos)      { border-color: var(--q-border-md); }

/* Botón descargar */
.btn-descargar {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 7px 16px;
  background: var(--q-gold);
  color: var(--q-gold-on);
  border: none;
  border-radius: 8px;
  font-size: 0.85rem;
  font-weight: 700;
  cursor: pointer;
  transition: opacity .15s;
  white-space: nowrap;
  flex-shrink: 0;
}
.btn-descargar:disabled { opacity: .6; cursor: not-allowed; }

/* Paginación */
.tp-paginacion {
  display: flex;
  flex-direction: row !important;
  align-items: center;
  gap: 10px;
  justify-content: center;
  margin-top: 1rem;
}
.btn-pag {
  width: 32px;
  height: 32px;
  border: 1px solid var(--q-border);
  border-radius: 6px;
  background: var(--q-card-alt);
  color: var(--q-text);
  font-size: 1rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: .15s;
}
.btn-pag:hover:not(:disabled) { border-color: var(--q-gold); color: var(--q-gold); }
.btn-pag:disabled { opacity: .4; cursor: not-allowed; }
.pag-info { font-size: 0.82rem; color: var(--q-muted); }

/* Columna Ver */
.td-ver { width: 36px; padding: 4px 6px !important; }
.btn-ver {
  width: 28px; height: 28px;
  border: 1px solid var(--q-border-md);
  border-radius: 7px;
  background: var(--q-card-alt);
  color: var(--q-muted);
  font-size: 0.78rem;
  cursor: pointer;
  display: flex; align-items: center; justify-content: center;
  transition: .15s;
}
.btn-ver:hover { border-color: var(--q-gold); color: var(--q-gold); background: var(--q-hover); }

/* Modal overlay */
.q-modal-overlay {
  position: fixed; inset: 0;
  background: rgba(10,15,25,.55);
  backdrop-filter: blur(7px);
  display: flex; align-items: flex-start; justify-content: center;
  z-index: 9999; padding: 14px; overflow-y: auto;
  -webkit-overflow-scrolling: touch;
}
.q-modal {
  width: 100%; max-width: 680px;
  background: linear-gradient(180deg, var(--q-card) 0%, var(--q-card-alt) 100%);
  border-radius: 18px; border: 1px solid var(--q-border);
  box-shadow: 0 18px 55px rgba(0,0,0,.28);
  overflow: hidden; margin: 14px auto;
  max-height: calc(100vh - 28px);
  display: flex; flex-direction: column;
}
.q-modal-head {
  padding: 16px 18px;
  display: flex; justify-content: space-between; align-items: center;
  border-bottom: 1px solid var(--q-border); flex-shrink: 0;
}
.q-modal-close { border: 0; background: transparent; font-size: 20px; line-height: 1; opacity: .7; cursor: pointer; }
.q-modal-close:hover { opacity: 1; }
.q-modal-body { padding: 18px; overflow-y: auto; flex: 1 1 auto; }
.q-modal-foot {
  padding: 12px 18px 16px 18px;
  display: flex; align-items: center; justify-content: flex-end; gap: 10px;
  border-top: 1px solid var(--q-border); flex-shrink: 0;
}

/* Grilla de campos */
.det-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 12px;
}
.det-field {
  background: var(--q-card-alt);
  border: 1px solid var(--q-border);
  border-radius: 10px;
  padding: 10px 12px;
}
.det-label { font-size: 11px; opacity: .6; text-transform: uppercase; letter-spacing: .4px; margin-bottom: 4px; }
.det-value { font-size: 0.88rem; font-weight: 600; color: var(--q-text); word-break: break-all; }
.det-precio { color: var(--q-gold); font-family: var(--q-font-mono); }
.det-mono { font-family: var(--q-font-mono); font-size: 0.78rem; }
.det-small { font-size: 0.72rem; }
.det-tg-nombre { font-size: 0.82rem; color: var(--q-muted); font-weight: 400; }

/* Sección invite */
.det-section { border-top: 1px solid var(--q-border); padding-top: 14px; }
.det-section-title { font-size: 0.78rem; text-transform: uppercase; letter-spacing: .5px; color: var(--q-muted); font-weight: 700; margin-bottom: 12px; }
.det-empty { font-size: 0.85rem; color: var(--q-muted); font-style: italic; }
.det-invite { display: flex; flex-direction: column; gap: 6px; }
.det-invite-row { display: flex; flex-direction: column; gap: 4px; }
.det-link {
  font-family: var(--q-font-mono); font-size: 0.78rem;
  color: var(--q-gold); word-break: break-all;
  text-decoration: none;
}
.det-link:hover { text-decoration: underline; }
.det-usado { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.det-sin-usar { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.det-usado-info { font-size: 0.82rem; color: var(--q-muted); }
.badge-usado {
  display: inline-block; padding: 2px 9px; border-radius: 20px; font-size: 0.72rem; font-weight: 700;
  background: var(--q-green-soft); color: var(--q-green);
}
.badge-sinusar {
  display: inline-block; padding: 2px 9px; border-radius: 20px; font-size: 0.72rem; font-weight: 700;
  background: var(--q-hover); color: var(--q-muted); border: 1px solid var(--q-border-md);
}

/* Resultado regeneración */
.det-regen-msg { padding: 10px 14px; border-radius: 10px; font-size: 0.85rem; font-weight: 600; }
.det-regen-ok  { background: var(--q-green-soft); color: var(--q-green); }
.det-regen-err { background: var(--q-red-soft, rgba(239,68,68,.12)); color: var(--q-red, #ef4444); }
.det-regen-enlaces {
  background: var(--q-card-alt);
  border: 1px solid var(--q-border);
  border-radius: 10px;
  padding: 12px 14px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.det-link--revocado {
  font-family: var(--q-font-mono);
  font-size: 0.78rem;
  color: var(--q-muted);
  text-decoration: line-through;
  word-break: break-all;
}
.det-link--nuevo {
  font-size: 0.85rem;
  font-weight: 700;
  word-break: break-all;
}
.badge-revocado {
  display: inline-block; padding: 1px 7px; border-radius: 20px; font-size: 10px; font-weight: 700;
  background: var(--q-red-soft, rgba(239,68,68,.12)); color: var(--q-red, #ef4444);
  margin-left: 6px; text-transform: none; letter-spacing: 0;
}
.badge-nuevo {
  display: inline-block; padding: 1px 7px; border-radius: 20px; font-size: 10px; font-weight: 700;
  background: rgba(255,193,7,.15); color: #ffc107;
  margin-left: 6px; text-transform: none; letter-spacing: 0;
}

/* Botones del footer */
.btn-regen {
  display: inline-flex; align-items: center; gap: 6px;
  padding: 7px 16px; background: var(--q-card-alt);
  border: 1px solid var(--q-gold); color: var(--q-gold);
  border-radius: 8px; font-size: 0.85rem; font-weight: 700;
  cursor: pointer; transition: .15s;
}
.btn-regen:hover:not(:disabled) { background: var(--q-gold); color: var(--q-gold-on); }
.btn-regen:disabled { opacity: .5; cursor: not-allowed; }
.btn-cerrar {
  background: var(--q-card-alt); border: 1px solid var(--q-border-md);
  color: var(--q-muted); padding: 7px 20px; border-radius: 8px;
  font-size: 0.85rem; font-weight: 600; cursor: pointer; transition: .15s;
}
.btn-cerrar:hover { background: var(--q-hover); color: var(--q-text); }

/* Badge tipo pago */
.badge-tipo {
  font-size: 0.72rem;
  margin-left: 4px;
  opacity: .85;
  vertical-align: middle;
}

/* Columna Pagado */
.td-pagado { white-space: nowrap; }
.pagado-wrap {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
}

.motivo-tag {
  display: inline-block;
  font-size: 0.65rem;
  font-weight: 700;
  letter-spacing: .03em;
  color: var(--q-gold);
  background: rgba(var(--q-gold-rgb, 255,193,7), .12);
  border: 1px solid rgba(var(--q-gold-rgb, 255,193,7), .25);
  border-radius: 4px;
  padding: 1px 5px;
  line-height: 1.4;
}

.badge-trial {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 20px;
  font-size: 0.7rem;
  font-weight: 700;
  background: rgba(139, 92, 246, 0.15);
  color: #a78bfa;
  border: 1px solid rgba(139, 92, 246, 0.3);
}

.precio-pagado {
  font-weight: 700;
  color: var(--q-green);
  font-family: var(--q-font-mono);
}
.precio-pagado.sin-cobro {
  color: var(--q-text-muted, #6c757d);
  font-weight: 400;
}

.det-proximo-pago {
  color: var(--q-gold);
  font-weight: 700;
  font-family: var(--q-font-mono);
}

/* Stripe error */
.stripe-loading {
  display: flex;
  align-items: center;
  font-size: 0.85rem;
  color: var(--q-muted);
  font-style: italic;
}
.stripe-error-box {
  background: rgba(220, 60, 30, 0.1);
  border: 1px solid rgba(220, 60, 30, 0.3);
  border-radius: 10px;
  padding: 10px 14px;
  font-size: 0.88rem;
  font-weight: 600;
  color: #d63b1e;
}

@media (max-width: 576px) {
  .q-modal-head, .q-modal-body, .q-modal-foot { padding-left: 14px; padding-right: 14px; }
  .det-grid { grid-template-columns: 1fr 1fr; }
}

/* Fecha último cobro en modal detalle */
.det-cobro-fecha {
  display: block;
  font-size: 0.72rem;
  color: var(--q-muted);
  font-weight: 400;
  margin-top: 2px;
}

/* Botón histórico */
.btn-historial {
  border-color: var(--q-border-md);
  color: var(--q-text);
}
.btn-historial:hover:not(:disabled) { background: var(--q-hover); color: var(--q-text); }

/* Histórico inline */
.hist-loading {
  display: flex; align-items: center;
  font-size: 0.85rem; color: var(--q-muted); font-style: italic;
}
.hist-timeline {
  display: flex;
  flex-direction: column;
  gap: 0;
  position: relative;
  padding-left: 24px;
}
.hist-timeline::before {
  content: '';
  position: absolute;
  left: 7px; top: 10px; bottom: 10px;
  width: 2px;
  background: var(--q-border);
}
.hist-item {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  padding: 10px 0;
  position: relative;
}
.hist-dot {
  width: 14px; height: 14px;
  border-radius: 50%;
  border: 2px solid var(--q-border-md);
  background: var(--q-card);
  flex-shrink: 0;
  margin-left: -24px;
  margin-top: 2px;
  z-index: 1;
  position: relative;
}
.hist-pago_ok .hist-dot, .hist-pago_recuperado .hist-dot { border-color: var(--q-green); background: var(--q-green-soft); }
.hist-pago_fallido .hist-dot { border-color: var(--q-red, #ef4444); background: rgba(239,68,68,.1); }
.hist-cancelada .hist-dot, .hist-vencida .hist-dot { border-color: var(--q-muted); background: var(--q-card-alt); }
.hist-creada .hist-dot { border-color: #8b5cf6; background: rgba(139,92,246,.12); }
.hist-content { flex: 1; }
.hist-label {
  font-size: 0.85rem;
  font-weight: 700;
  color: var(--q-text);
}
.hist-pago_ok .hist-label, .hist-pago_recuperado .hist-label { color: var(--q-green); }
.hist-pago_fallido .hist-label { color: var(--q-red, #ef4444); }
.hist-cancelada .hist-label, .hist-vencida .hist-label { color: var(--q-muted); }
.hist-creada .hist-label { color: #a78bfa; }
.hist-fecha {
  font-size: 0.75rem;
  color: var(--q-muted);
  margin-top: 1px;
}
.hist-importe {
  font-size: 0.82rem;
  font-weight: 700;
  font-family: var(--q-font-mono);
  color: var(--q-green);
  margin-top: 2px;
}
.hist-importe--trial {
  color: #a78bfa;
  font-family: inherit;
  font-weight: 600;
}
.hist-motivo { font-size: 0.75rem; color: var(--q-red, #ef4444); opacity: .85; margin-top: 2px; }
</style>
